import { AbstractControl, ValidatorFn } from "@angular/forms";
import { WellStub, Well, GISPoint, GISPointTypes } from "../../common/models/well.model";
import { Address } from "../../common/models/address.model";
import { ParserError, ErrorSeverity } from "../../builder/components/state/controls/config/data/file-parser-config/parser.model";
import { IUploadFile } from "../../common/services/document-management.service";
import { NoteType } from '../../common/models/note-type.enum';

export interface ComplianceReportingSearchResult {
	id: string;
	countyName?: string;
	townshipName?: string;
	wellName?: string;
	wellNumber?: string;
	leaseName?: string;
	leaseId?: string;
	organizationId?: string;
	organizationName?: string;
}

export interface ComplianceReportingType {
    typeId: string;
    description: string;
    apiNumbers: WellSelectionMode;
    reviewGroup: string;
    dueDateDescription: string;
    isAnnual: boolean;
    isQuarterly: boolean;
    isEnabled: boolean;
}

export enum WellSelectionMode {
    None = "None",
    Single = "Single", 
    Multiple = "Multiple"
}

export enum FileProcessingMode {
    None = "None",
    Upload = "Upload",
    Parser = "Parser"
}

export enum ReportingMethod {
    None = "",
    Manual = "manual",
    Upload = "upload"
}

export class WellProductionModel {
    apiNumber: string;
    brineProduced: number;
    domesticUserAddress: string;
    domesticUserCity: string;
    domesticUserState: string;
    domesticUserZipCode: string;
    county: string;
    dateOfFirstProduction: Date;
    daysInProduction: number;
    domesticUserName: string;
    domesticUserPhone: string;
    gasProduced: number;
    gasSold: number;
    isManualAddition: boolean = false;
    newOwner: string;
    oilProduced: number;
    oilSold: number;
    organizationId: string;
    organizationName: string;
    reportedByOrganizationId: string;
    reviewStatus: string;
    statusChange: boolean;
    statusChangeDate: Date;    
    township: string
    wellName: string;
    wellNumber: string;  

    constructor(well: Well) {
        this.apiNumber = well.id;
        this.wellName = (well.lease != null) ? well.lease.name : well.name;
        this.wellNumber = well.wellNumber;
        this.organizationId = well.organizationId;
        if (well.location != null && well.location.gisPoints != null) {
            const wellHead = well.location.gisPoints.find(g => g.pointType === GISPointTypes.WellHead);
            if (wellHead != null) {
                if (wellHead.county != null) {
                    this.county = wellHead.county.name;
                }
                if (wellHead.township != null) {
                    this.township = wellHead.township.name;
                }
            }         
        } 
    }
}


export class WellProductionWithErrorsModel extends WellProductionModel {
    errors: ParserError[] = [];


    constructor(well: Well) {
        super(well);
    }
}

export class ProductionTankData {
    associatedApiNumbers: string;
    county: string;
    latitude: number;
    longitude: number;
    lot: string;
    name: string;
    municipality: string;
    numberOfTanks: number;
    reviewStatus: string;
    section: string;
    tank1Contents: number;
    tank1Storage: number;
    tank2Contents: number;
    tank2Storage: number;
    tank3Contents: number;
    tank3Storage: number;
    tank4Contents: number;
    tank4Storage: number;
    tank5Contents: number;
    tank5Storage: number;
    tank6Contents: number;
    tank6Storage: number;
    tank7Contents: number;
    tank7Storage: number;
    tank8Contents: string;
    tank8Storage: number;
    tank9Contents: number;
    tank9Storage: number;
    tank10Contents: number;
    tank10Storage: number;
    tank11Contents: number;
    tank11Storage: number;
    tank12Contents: number;
    tank12Storage: number;
    totalStorage: number;
    township: string;
}

export function noCriticalErrors(): ValidatorFn {
    return (ctrl: AbstractControl): { [key: string]: any | null } => {
        const value: ParserError[] = <ParserError[]>ctrl.value;
        let valid: boolean = value.filter(p => p.severity === ErrorSeverity.Critical).length === 0;
        return (valid) ? null : { "criticalErrors": "Data has critical errors." };
    }
}

export enum ComplianceReportStatus {
    Pending = "Pending",
    Approved = "Approved",
    Denied = "Denied",
    Updated = "Updated",
    Rejected = "Rejected",
    Unsubmitted = "Unsubmitted"
}

export class ComplianceReportPreparer {
    id?: string;
    reportId?: string;
    name: string;
    email: string;
    officePhone: string;
    phone: string;
}

export class ComplianceReport {
    id?: string;
    typeId: string;
    reportingYear: number;
    reportingQuarter: string;
    confidentiality?: boolean;
    solutionProject: string;
    organization: string;
    ownerName?: string;
    streetAddress?: string;
    suite?: string;
    poBox?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    emergencyPhone?: string;    
    reportingMethod: string;
    reviewStatus: string;
    assignedGroupId: string;
    reviewedBy?: string;
    createdDate: Date;
    createdById: string;
    reviewedDate?: Date;
    modification?: boolean;
    modificationReason?: string;
    type: ComplianceReportingType;
    wellDirection: string;
    internal: boolean;
    complianceReportPreparer: ComplianceReportPreparer;
    complianceReportFiles: ComplianceReportFile[] = [];
    complianceReportWells: ComplianceReportWell[] = [];
    complianceReportNotes: ReportNote[] = [];
    productionReportingDatas: WellProductionModel[] =[];
    productionTankDatas: ProductionTankData[] = [];  
}

export class ComplianceLogs {
    id?: string;
    typeId: string;
    reviewStatus: string;
    confidentiality?: boolean;
    reportingMethod: string;
    organization: string;
    ownerName?: string;
    internal: boolean;
    complianceReportFiles: ComplianceReportFile[] = [];
    complianceReportWells: ComplianceReportWell[] = [];
    typeOfLogs: string;
    loggingCompany: string;
}

export class ComplianceReportFile {
    id?: string;
    reportId?: string;
    blobUri: string;
    fileName: string;
    extension: string;
    type: string;
}

export class ComplianceReportWell {
    id?: string;
    reportId?: string;
    apiNumber: string;
    reviewStatus: string;
}


export class ParseProductionDataResponse {
    reportId: string;
    file: IUploadFile;
    ownerName: string;
    ownerNumber: string;
    address: Address;
    emergencyPhone: string;
    isModification: boolean;
    modificationReason: string;
    preparer: ComplianceReportPreparer;
    productionData: WellProductionModel[];
    tankData: ProductionTankData[];
    errors: ParserError[]
    quarterReported: string;
    yearReported: number;
}

export class ComplianceReportSearchSpec {
    ids?: string[];
    organizations?: string[];
    apiNumbers?: string[];
    approvalState?: string;
    includeChildren: boolean = true;
    internal?: boolean;
    periods: ComplianceReportingPeriod[]

    constructor(includeChildren: boolean) {
        this.includeChildren = includeChildren;
    }
}

export interface ComplianceReportingPeriod {
    year: number;
    quarter?: string;
}

export interface ReportNote {
    createdDate: Date | string;
    createdByName: string;
    createdByUserId: string;
    id?: string;
    reportId: string;
    text: string;
    type: NoteType;
}
